<template>
  <div class="top-bar-breadcrumb">
    <div
      class="top-bar-logo-slot"
      role="button"
      tabindex="0"
      aria-label="Go to home"
      @click="navigateHome"
      @keydown.enter.space.prevent="navigateHome"
    >
      <img
        src="/img/brand/AVA_Logo_Mint.svg"
        alt="AVA"
        class="top-bar-logo"
      />
    </div>

    <breadcrumb-separator />

    <company-segment :user="user" />

    <template v-if="isInsideFacility">
      <breadcrumb-separator />
      <facility-segment />
    </template>

    <template v-if="isVerificationWorkspace">
      <breadcrumb-separator />
      <verification-segment />
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import BreadcrumbSeparator from './BreadcrumbSeparator.vue';
import CompanySegment from './CompanySegment.vue';
import FacilitySegment from './FacilitySegment.vue';
import VerificationSegment from './VerificationSegment.vue';
import { log as debugLog } from '@/utils/debugLogger';
import { isAnyViewerOpen } from '@/scripts/js/forgeviewer';

export default {
  name: 'TopBarBreadcrumb',
  components: {
    BreadcrumbSeparator,
    CompanySegment,
    FacilitySegment,
    VerificationSegment,
  },
  props: {
    user: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapGetters('FACILITY', {
      currentFacility: 'GET_FACILITY',
      accessibleEntries: 'GET_ACCESSIBLE_ENTRIES',
    }),
    isInsideFacility() {
      return this.$route.matched.some(
        (record) => record.meta && record.meta.facilityScope
      );
    },
    isVerificationWorkspace() {
      return this.$route.name === 'verification-workspace';
    },
    routeFacilityId() {
      return this.$route.params.id || null;
    },
  },
  watch: {
    routeFacilityId: {
      immediate: true,
      handler(newId) {
        this.syncFacilityFromRoute(newId);
      },
    },
  },
  methods: {
    navigateHome() {
      if (this.$route.path === '/') return;

      // Preserves the safety behavior previously owned by the side-rail's
      // asset-header: warn when a viewer is open, clean up panels + collapse
      // the rail, then route. Without this port, users inside a viewer could
      // lose work by clicking the logo.
      const goHome = () => {
        this.$store.dispatch('GENERAL/CLOSE_ALL_PANELS');
        this.$store.dispatch('GENERAL/COLLAPSE_RAIL');
        this.$router.push('/');
      };

      if (isAnyViewerOpen()) {
        this.$dialog.warning({
          text: 'You have a viewer open. Navigating away will close it. Are you sure you want to continue?',
          title: 'Close Viewer',
          actions: {
            false: 'Cancel',
            true: {
              text: 'Continue',
              color: 'primary',
              handle: goHome,
            },
          },
        });
        return;
      }

      goHome();
    },
    syncFacilityFromRoute(newId) {
      // Defensive fallback: the primary facility-page components (MainView, AdminView)
      // already trigger SET_FACILITY via GET_BY_ID on mount. This watcher only steps
      // in when the store pointer is stale relative to the current route — e.g.,
      // after the user clicks a new facility from the breadcrumb dropdown while
      // already inside a facility route, or on deep-link cold loads if the page
      // component hasn't populated the store yet.
      if (!this.isInsideFacility || !newId) return;

      const current = this.currentFacility;
      if (current && current.id === newId) return;

      // Prefer the cached accessible list (no network) when the target is there.
      const fromCache = this.accessibleEntries.find((f) => f.id === newId);
      if (fromCache) {
        debugLog('[TopBarBreadcrumb] sync facility from cache', newId);
        this.$store.commit('FACILITY/SET_FACILITY', fromCache);
        return;
      }

      // Otherwise fetch. GET_BY_ID is idempotent and any duplicate call from
      // a concurrently-mounting facility view simply re-commits the same value.
      debugLog('[TopBarBreadcrumb] sync facility via GET_BY_ID', newId);
      this.$store.dispatch('FACILITY/GET_BY_ID', {
        id: newId,
        selectFacility: true,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.top-bar-breadcrumb {
  display: inline-flex;
  align-items: center;
  flex-wrap: nowrap;
  min-width: 0;
  height: 100%;
}

/* The logo occupies a column equal in width to the collapsed side rail below it,
   creating a clean vertical alignment between the top-bar brand mark and the rail.
   The whole column is the click target, not just the logo pixels. */
.top-bar-logo-slot {
  width: var(--ds-nav-rail-collapsed);
  padding: 8px;
  height: 100%;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color var(--ds-transition-fast);

  &:hover {
    background-color: rgba(255, 255, 255, 0.04);
  }

  &:focus-visible {
    outline: 2px solid var(--v-primary-base);
    outline-offset: -2px;
  }
}

.top-bar-logo {
  height: 22px;
  max-width: 100%;
  object-fit: contain;
  transition: opacity var(--ds-transition-fast);
  pointer-events: none;
}

.top-bar-logo-slot:hover .top-bar-logo {
  opacity: 0.9;
}
</style>
